import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { FiUpload, FiDownload, FiCheckCircle, FiXCircle, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import Lottie from 'lottie-react';
import nvgtrLoader from '../../../loaders/nvgtr_loader_white.json';
import nvgtrLoaderWhite from '../../../loaders/nvgtr_loader_white.json';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const BrandSection = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [brandAssets, setBrandAssets] = useState(null);
  const [customDomain, setCustomDomain] = useState(null);
  const [customDomainStatus, setCustomDomainStatus] = useState('not-set');
  const [uploadingType, setUploadingType] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [sidebarLogoPreference, setSidebarLogoPreference] = useState('brandmark'); // 'brandmark' or 'full'
  const [savingPreference, setSavingPreference] = useState(false);
  const { darkMode } = useTheme();

  // Helper to get proper asset URL
  const getAssetUrl = (url) => {
    if (!url) return null;
    
    // If it's an S3 URL, use the proxy
    if (url.includes('amazonaws.com')) {
      const urlParts = url.split('/');
      const fileKey = urlParts.slice(3).join('/');
      return `${process.env.REACT_APP_API_URL}/brand/image-proxy?key=${encodeURIComponent(fileKey)}`;
    }
    
    // Otherwise use directly
    return url;
  };

  // Helper function to check if a URL is an SVG file
  const isSvgFile = (url) => {
    if (!url) return false;
    
    // Extract just the filename part (before any query parameters)
    const baseUrl = url.split('?')[0];
    // Check if .svg appears anywhere in the filename (more lenient check)
    const result = baseUrl.toLowerCase().includes('.svg');
    console.log(`SVG detection for ${url}: ${result ? 'SVG' : 'Not SVG'} (base: ${baseUrl})`);
    return result;
  };

  // Add a helper function to extract just the file name without path or query params
  const extractFileName = (url) => {
    if (!url) return 'Uploaded file';
    
    // Remove query parameters
    const baseUrl = url.split('?')[0];
    
    // Get just the filename (after the last slash)
    const parts = baseUrl.split('/');
    let fileName = parts[parts.length - 1];
    
    // Remove any UUID prefix (anything before the first dash)
    if (fileName.includes('-')) {
      const nameParts = fileName.split('-');
      // If first part looks like a UUID (has proper length and only hex chars)
      if (nameParts[0].length > 20 && /^[0-9a-f-]+$/.test(nameParts[0])) {
        // Remove the first part and the dash
        fileName = nameParts.slice(1).join('-');
      }
    }
    
    return fileName;
  };

  // Fetch brand assets status on component mount
  useEffect(() => {
    const fetchBrandAssets = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        
        console.log('Fetching brand assets with token:', token ? 'Token exists' : 'No token');
        
        // First get organization info to check if admin and organization
        const orgResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/organization`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        let isOrganizationAdmin = false;
        let orgId = null;
        
        if (orgResponse.data.hasOrganization) {
          const orgRole = orgResponse.data.organization.userRole;
          isOrganizationAdmin = orgRole === 'admin' || orgRole === 'owner';
          orgId = orgResponse.data.organization.id;
        }
        
        // For non-admins, still fetch the organization branding for display purposes,
        // but mark that they can't edit it
        if (!isOrganizationAdmin && orgId) {
          try {
            // Get the sidebar branding that's already available, which should include
            // the organization branding if it exists
            const brandResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/brand/status`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (brandResponse.data.brandAssets?.isProcessed) {
              // We display the assets read-only
              setBrandAssets(brandResponse.data.brandAssets);
              setCustomDomain(brandResponse.data.customDomain);
              setCustomDomainStatus('not-admin');
            } else {
              setCustomDomainStatus('not-admin');
            }
            return;
          } catch (error) {
            // If there's an error, just show the "not-admin" message
            setCustomDomainStatus('not-admin');
            return;
          }
        }
        
        if (!isOrganizationAdmin) {
          // If not admin and no org data available
          setCustomDomainStatus('not-admin');
          return;
        }
        
        // Get brand assets - this will check both user and organization assets
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/brand/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log('Brand assets API response:', response.data);
        
        setBrandAssets(response.data.brandAssets);
        setCustomDomain(response.data.customDomain);
        setCustomDomainStatus(response.data.customDomainStatus);
        
        // Get sidebar logo preference if it exists
        if (response.data.sidebarLogoPreference) {
          setSidebarLogoPreference(response.data.sidebarLogoPreference);
        }
      } catch (error) {
        console.error('Error fetching brand assets:', error);
        
        // Log more details about the error
        if (error.response) {
          console.log('Error response data:', error.response.data);
          console.log('Error status:', error.response.status);
          
          // Check if the error is related to custom domain
          if (error.response.data.customDomainStatus) {
            console.log('Setting customDomainStatus to:', error.response.data.customDomainStatus);
            setCustomDomainStatus(error.response.data.customDomainStatus);
          }
          
          toast.error(error.response.data.error || 'Failed to fetch brand assets');
        } else {
          toast.error('Failed to fetch brand assets');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchBrandAssets();
  }, []);

  // Toggle upload form display
  const handleShowUploadForm = () => {
    setShowUploadForm(true);
  };

  // Hide upload form and keep existing assets
  const handleCancelUpload = () => {
    setShowUploadForm(false);
  };

  // When uploading new assets for a processed brand, we need to mark it for reprocessing
  const resubmitToAdmin = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Call the resubmit endpoint to mark the assets for reprocessing
      await axios.post(
        `${process.env.REACT_APP_API_URL}/brand/resubmit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // We don't need to update local state as the upload handlers will do that
    } catch (error) {
      console.error('Error marking assets for reprocessing:', error);
      // We don't need to show an error as this is a background operation
    }
  };

  // Save sidebar logo preference
  const saveSidebarLogoPreference = async (preference) => {
    if (savingPreference) return;
    const previousPreference = sidebarLogoPreference;
    try {
      setSavingPreference(true);
      setSidebarLogoPreference(preference);
      // Live update: dispatch so Sidebar fetches and updates (same pattern as AccountModal's useFullLogo reading from cache)
      window.dispatchEvent(new CustomEvent('sidebar-logo-preference-changed', { 
        detail: { preference } 
      }));
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/brand/sidebar-logo-preference`,
        { preference },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Sidebar logo preference updated');
      
    } catch (error) {
      console.error('Error saving sidebar logo preference:', error);
      setSidebarLogoPreference(previousPreference);
      // Revert sidebar to previous preference on failure
      window.dispatchEvent(new CustomEvent('sidebar-logo-preference-changed', { 
        detail: { preference: previousPreference } 
      }));
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to save preference');
      }
    } finally {
      setSavingPreference(false);
    }
  };

  // Handle file upload for brand mark
  const onDropBrandMark = useCallback(async (acceptedFiles) => {
    try {
      if (acceptedFiles.length === 0) return;
      
      // Check file type
      const file = acceptedFiles[0];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      if (!['svg', 'ai'].includes(fileExtension)) {
        toast.error('Only .svg and .ai files are allowed');
        return;
      }
      
      setUploadingType('brandMark');
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/brand/upload/brand-mark`,
        formData,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      setBrandAssets(response.data.brandAssets);
      toast.success('Brand mark uploaded successfully');
      
      // If this was a processed brand and the user is updating, mark for reprocessing
      if (brandAssets?.isProcessed) {
        await resubmitToAdmin();
      }
    } catch (error) {
      console.error('Error uploading brand mark:', error);
      
      if (error.response && error.response.data) {
        toast.error(error.response.data.error || 'Failed to upload brand mark');
      } else {
        toast.error('Failed to upload brand mark');
      }
    } finally {
      setUploadingType(null);
    }
  }, [brandAssets?.isProcessed]);

  // Handle file upload for brand mark and word mark
  const onDropBrandMarkWord = useCallback(async (acceptedFiles) => {
    try {
      if (acceptedFiles.length === 0) return;
      
      // Check file type
      const file = acceptedFiles[0];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      if (!['svg', 'ai'].includes(fileExtension)) {
        toast.error('Only .svg and .ai files are allowed');
        return;
      }
      
      setUploadingType('brandMarkWord');
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/brand/upload/brand-mark-word`,
        formData,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      setBrandAssets(response.data.brandAssets);
      toast.success('Brand mark and word mark uploaded successfully');
      
      // If this was a processed brand and the user is updating, mark for reprocessing
      if (brandAssets?.isProcessed) {
        await resubmitToAdmin();
      }
    } catch (error) {
      console.error('Error uploading brand mark and word mark:', error);
      
      if (error.response && error.response.data) {
        toast.error(error.response.data.error || 'Failed to upload brand mark and word mark');
      } else {
        toast.error('Failed to upload brand mark and word mark');
      }
    } finally {
      setUploadingType(null);
    }
  }, [brandAssets?.isProcessed]);

  // Setup dropzone for brand mark
  const { getRootProps: getRootPropsBrandMark, getInputProps: getInputPropsBrandMark } = useDropzone({
    onDrop: onDropBrandMark,
    accept: {
      'image/svg+xml': ['.svg'],
      'application/postscript': ['.ai']
    },
    maxFiles: 1,
    disabled: uploadingType !== null
  });

  // Setup dropzone for brand mark and word mark
  const { getRootProps: getRootPropsBrandMarkWord, getInputProps: getInputPropsBrandMarkWord } = useDropzone({
    onDrop: onDropBrandMarkWord,
    accept: {
      'image/svg+xml': ['.svg'],
      'application/postscript': ['.ai']
    },
    maxFiles: 1,
    disabled: uploadingType !== null
  });

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4" style={{ fontFamily: 'var(--chat-font-family)' }}>
        <SkeletonTheme baseColor={darkMode ? "#181818" : "#f5f5f5"} highlightColor={darkMode ? "#282828" : "#e9e9e9"}>
          {/* Upload Cards Skeleton - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Card 1 */}
            <div className={`border rounded-xl p-6 ${darkMode ? 'border-[#282828]' : 'border-[#e9e9e9]'}`}>
              <div className="mb-4">
                <Skeleton height={20} width={120} className="mb-1" />
                <Skeleton height={14} width={100} />
              </div>
              <div className="mb-4">
                <Skeleton height={96} className="rounded-lg mb-2" />
                <Skeleton height={12} width="70%" />
              </div>
              <div className={`border-2 border-dashed rounded-lg p-6 ${darkMode ? 'border-[#282828]' : 'border-[#e9e9e9]'}`}>
                <Skeleton height={20} width={20} className="mx-auto mb-2" />
                <Skeleton height={14} width={80} className="mx-auto mb-1" />
                <Skeleton height={12} width={100} className="mx-auto" />
              </div>
            </div>

            {/* Card 2 */}
            <div className={`border rounded-xl p-6 ${darkMode ? 'border-[#282828]' : 'border-[#e9e9e9]'}`}>
              <div className="mb-4">
                <Skeleton height={20} width={100} className="mb-1" />
                <Skeleton height={14} width={90} />
              </div>
              <div className="mb-4">
                <Skeleton height={96} className="rounded-lg mb-2" />
                <Skeleton height={12} width="70%" />
              </div>
              <div className={`border-2 border-dashed rounded-lg p-6 ${darkMode ? 'border-[#282828]' : 'border-[#e9e9e9]'}`}>
                <Skeleton height={20} width={20} className="mx-auto mb-2" />
                <Skeleton height={14} width={80} className="mx-auto mb-1" />
                <Skeleton height={12} width={100} className="mx-auto" />
              </div>
            </div>
          </div>
        </SkeletonTheme>
      </div>
    );
  }

  // Render not-admin message with branding preview if available
  if (customDomainStatus === 'not-admin') {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4" style={{ fontFamily: 'var(--chat-font-family)' }}>
        <div className={`border rounded-2xl p-4 mb-6 ${
          darkMode 
            ? 'bg-red-900/20 border-red-700' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <FiAlertTriangle className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${darkMode ? 'text-red-200' : 'text-red-800'}`}>
                Admin Privileges Required
              </h3>
              <div className={`mt-2 text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                <p>
                  Only organization administrators can manage brand assets. Contact your organization administrator for assistance.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Display current brand assets if they exist (read-only) */}
        {brandAssets?.isProcessed && (
          <section className="pt-5 border-t border-gray-100 dark:border-[#2a2a2a]">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">Current Organization Branding</h3>
            
            {/* Brand Mark */}
            {brandAssets.brandMark?.original && (
              <div className={`border rounded-xl p-4 mb-4 ${darkMode ? 'border-[#282828]' : 'border-[#e9e9e9]'}`}>
                <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-[#030303]'}`}>Brand Mark</h4>
                <div className={`max-w-xs h-24 flex items-center justify-center rounded-xl p-2 ${darkMode ? 'bg-[#181818]' : 'bg-[#f5f5f5]'}`}>
                  {isSvgFile(brandAssets.brandMark.original) ? (
                    <img 
                      src={getAssetUrl(brandAssets.brandMark.original)} 
                      alt="Brand Mark" 
                      className="max-h-full max-w-full object-contain" 
                      onError={(e) => {
                        console.error('Error loading Brand Mark image:', e.target.src);
                        e.target.onerror = null; // Prevent infinite error loops
                      }}
                    />
                  ) : (
                    <div className={`flex flex-col items-center justify-center ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`}>
                      <FiDownload className="h-8 w-8 mb-1" />
                      <span className="text-xs">AI File (Preview not available)</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Brand Mark Word Mark */}
            {brandAssets.brandMarkWordMark?.original && (
              <div className={`border rounded-xl p-4 ${darkMode ? 'border-[#282828]' : 'border-[#e9e9e9]'}`}>
                <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-[#030303]'}`}>Brand Mark with Text</h4>
                <div className={`max-w-xs h-24 flex items-center justify-center rounded-xl p-2 ${darkMode ? 'bg-[#181818]' : 'bg-[#f5f5f5]'}`}>
                  {isSvgFile(brandAssets.brandMarkWordMark.original) ? (
                    <img 
                      src={getAssetUrl(brandAssets.brandMarkWordMark.original)} 
                      alt="Brand Mark with Text" 
                      className="max-h-full max-w-full object-contain" 
                    />
                  ) : (
                    <div className={`flex flex-col items-center justify-center ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`}>
                      <FiDownload className="h-8 w-8 mb-1" />
                      <span className="text-xs">AI File (Preview not available)</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    );
  }

  // When brand assets are processed and we're not showing the upload form
  if (brandAssets?.isProcessed && !showUploadForm) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4" style={{ fontFamily: 'var(--chat-font-family)' }}>
        <div className="space-y-0">
        <section className="pb-5 border-b border-gray-100 dark:border-[#2a2a2a] flex flex-wrap items-center justify-between gap-4">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Brand assets</h3>
          <button
            onClick={handleShowUploadForm}
            className="account-modal-btn inline-flex items-center px-2.5 py-1.5 border rounded-md shadow-sm text-xs font-medium text-white bg-black hover:bg-gray-800 dark:bg-white dark:text-black dark:border-white dark:hover:bg-gray-200"
          >
            <FiRefreshCw className="mr-1.5 h-3 w-3" />
            Change Brand Assets
          </button>
        </section>

        <section className="py-5 border-b border-gray-100 dark:border-[#2a2a2a]">
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-2xl p-6 mb-0">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <FiCheckCircle className="h-5 w-5 text-green-500" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                Brand Assets Active
              </h3>
              <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                <p>
                  Your brand assets have been processed and are now being used on your custom domain.
                </p>
                <p className="mt-1">
                  Processed on {new Date(brandAssets.processedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
        </section>

        {/* Sidebar Logo Preference Toggle */}
        {brandAssets?.brandMark?.original && brandAssets?.brandMarkWordMark?.original && (
          <section className="py-5 border-b border-gray-100 dark:border-[#2a2a2a]">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">Sidebar logo</h3>
            <div className="space-y-4">
              <p className={`text-sm ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`}>
                Choose which logo version to display in the sidebar navigation for your entire organization. Only organization administrators can change this setting.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Brandmark Only Option */}
                  <div 
                  className={`account-modal-item border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    sidebarLogoPreference === 'brandmark' 
                      ? darkMode 
                        ? 'border-white bg-[#181818]' 
                        : 'border-[#030303] bg-[#f5f5f5]'
                      : darkMode 
                        ? 'border-[#282828] hover:border-[#444444]' 
                        : 'border-[#e9e9e9] hover:border-[#d1d5db]'
                  }`}
                  onClick={() => !savingPreference && saveSidebarLogoPreference('brandmark')}
                >
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                      sidebarLogoPreference === 'brandmark' 
                        ? darkMode 
                          ? 'border-white bg-[#181818]' 
                          : 'border-[#030303] bg-[#030303]'
                        : darkMode 
                          ? 'border-[#444444]' 
                          : 'border-[#d1d5db]'
                    }`}>
                      {sidebarLogoPreference === 'brandmark' && (
                        <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                      )}
                    </div>
                    <span className={`font-medium ${darkMode ? 'text-white' : 'text-[#030303]'}`}>Brandmark</span>
                  </div>
                </div>

                {/* Full Logo Option */}
                <div 
                  className={`account-modal-item border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    sidebarLogoPreference === 'full' 
                      ? darkMode 
                        ? 'border-white bg-[#181818]' 
                        : 'border-[#030303] bg-[#f5f5f5]'
                      : darkMode 
                        ? 'border-[#282828] hover:border-[#444444]' 
                        : 'border-[#e9e9e9] hover:border-[#d1d5db]'
                  }`}
                  onClick={() => !savingPreference && saveSidebarLogoPreference('full')}
                >
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                      sidebarLogoPreference === 'full' 
                        ? darkMode 
                          ? 'border-white bg-[#181818]' 
                          : 'border-[#030303] bg-[#030303]'
                        : darkMode 
                          ? 'border-[#444444]' 
                          : 'border-[#d1d5db]'
                    }`}>
                      {sidebarLogoPreference === 'full' && (
                        <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                      )}
                    </div>
                    <span className={`font-medium ${darkMode ? 'text-white' : 'text-[#030303]'}`}>Full Logo</span>
                  </div>
                </div>
              </div>

              {savingPreference && (
                <div className="flex items-center justify-center py-2">
                  <Lottie
                    animationData={darkMode ? nvgtrLoaderWhite : nvgtrLoader}
                    loop={true}
                    style={{ width: 20, height: 20 }}
                  />
                  <span className={`ml-2 text-sm ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`}>Saving preference...</span>
                </div>
              )}
            </div>
          </section>
        )}
        </div>
      </div>
    );
  }

  // Calculate if both assets are uploaded
  const bothAssetsUploaded = brandAssets?.brandMark?.original && brandAssets?.brandMarkWordMark?.original;
  const isSubmitted = brandAssets?.isSubmitted && !brandAssets?.isProcessed;

  // Render upload form for unprocessed assets or when explicitly showing the form
  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4" style={{ fontFamily: 'var(--chat-font-family)' }}>
      <div className="space-y-0">
        <section className="pb-5 border-b border-gray-100 dark:border-[#2a2a2a] flex flex-wrap items-center justify-between gap-4">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Upload brand assets</h3>
          {brandAssets?.isProcessed && (
            <button
              onClick={handleCancelUpload}
              className="account-modal-btn px-4 py-2 rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-gray-50/50 dark:bg-[#181818] text-gray-900 dark:text-white text-sm font-medium hover:bg-gray-100 dark:hover:bg-[#252525]"
            >
              Cancel
            </button>
          )}
        </section>

      {/* Status Banner - Submitted */}
      {isSubmitted && (
        <section className="py-5 border-b border-gray-100 dark:border-[#2a2a2a]">
        <div className={`flex items-start gap-3 p-4 rounded-xl ${
          darkMode 
            ? 'bg-[#4A3F1A] border border-[#5A4F2A]' 
            : 'bg-[#FFF3CD] border border-[#FFE69C]'
        }`}>
          <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${darkMode ? 'text-[#FFE69C]' : 'text-[#856404]'}`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 className={`text-[15px] font-medium ${darkMode ? 'text-[#FFE69C]' : 'text-[#856404]'}`}>
              Submitted for Review
            </h3>
            <p className={`text-[14px] ${darkMode ? 'text-[#D4C390]' : 'text-[#856404]'}`}>
              We'll review your assets and activate them within 1-2 business days
            </p>
          </div>
        </div>
        </section>
      )}
      
      {/* Upload Cards - Side by Side */}
      <section className="py-5 border-b border-gray-100 dark:border-[#2a2a2a]">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Brand Mark Card */}
        <div className={`border rounded-xl p-6 ${darkMode ? 'border-[#282828] bg-[#111111]' : 'border-[#e9e9e9] bg-white'}`}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className={`text-base font-semibold mb-1 ${darkMode ? 'text-white' : 'text-[#030303]'}`}>1. Brand Mark</h3>
              <p className={`text-xs ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`}>
                Logo without text
              </p>
            </div>
            {brandAssets?.brandMark?.original && (
              <FiCheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            )}
          </div>
          
          {/* Preview if uploaded */}
          {brandAssets?.brandMark?.original && (
            <div className="mb-4">
              <div className={`h-32 flex items-center justify-center rounded-lg p-4 mb-2 ${darkMode ? 'bg-[#181818]' : 'bg-[#f5f5f5]'}`}>
                {isSvgFile(brandAssets.brandMark.original) ? (
                  <img 
                    src={getAssetUrl(brandAssets.brandMark.original)} 
                    alt="Brand Mark" 
                    className="h-full w-full object-contain" 
                    onError={(e) => {
                      console.error('Error loading Brand Mark image:', e.target.src);
                      e.target.onerror = null;
                    }}
                  />
                ) : (
                  <div className={`flex flex-col items-center justify-center ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`}>
                    <FiDownload className="h-6 w-6 mb-1" />
                    <span className="text-xs">AI File</span>
                  </div>
                )}
              </div>
              <p className={`text-xs truncate ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`}>
                {extractFileName(brandAssets.brandMark.original)}
              </p>
            </div>
          )}
          
          {/* Upload area */}
          <div
            {...getRootPropsBrandMark()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
              uploadingType === 'brandMark' 
                ? 'opacity-50 pointer-events-none' 
                : darkMode ? 'hover:bg-[#181818]' : 'hover:bg-[#f5f5f5]'
            } ${
              brandAssets?.brandMark?.original 
                ? 'border-green-300 dark:border-green-700' 
                : darkMode ? 'border-[#282828]' : 'border-[#d1d5db]'
            }`}
          >
            <input {...getInputPropsBrandMark()} />
            <div className="space-y-2">
              <div className="flex justify-center">
                {uploadingType === 'brandMark' ? (
                  <Lottie
                    animationData={darkMode ? nvgtrLoaderWhite : nvgtrLoader}
                    loop={true}
                    style={{ width: 24, height: 24 }}
                  />
                ) : (
                  <FiUpload className={`h-5 w-5 ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`} />
                )}
              </div>
              <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-[#030303]'}`}>
                {brandAssets?.brandMark?.original ? 'Replace file' : 'Upload file'}
              </div>
              <p className={`text-xs ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`}>
                .svg or .ai only
              </p>
            </div>
          </div>
        </div>
        
        {/* Brand Mark & Word Mark Card */}
        <div className={`border rounded-xl p-6 ${darkMode ? 'border-[#282828] bg-[#111111]' : 'border-[#e9e9e9] bg-white'}`}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className={`text-base font-semibold mb-1 ${darkMode ? 'text-white' : 'text-[#030303]'}`}>2. Full Logo</h3>
              <p className={`text-xs ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`}>
                Logo with text
              </p>
            </div>
            {brandAssets?.brandMarkWordMark?.original && (
              <FiCheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            )}
          </div>
          
          {/* Preview if uploaded */}
          {brandAssets?.brandMarkWordMark?.original && (
            <div className="mb-4">
              <div className={`h-32 flex items-center justify-center rounded-lg p-4 mb-2 ${darkMode ? 'bg-[#181818]' : 'bg-[#f5f5f5]'}`}>
                {isSvgFile(brandAssets.brandMarkWordMark.original) ? (
                  <img 
                    src={getAssetUrl(brandAssets.brandMarkWordMark.original)} 
                    alt="Full Logo" 
                    className="h-full w-full object-contain" 
                  />
                ) : (
                  <div className={`flex flex-col items-center justify-center ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`}>
                    <FiDownload className="h-6 w-6 mb-1" />
                    <span className="text-xs">AI File</span>
                  </div>
                )}
              </div>
              <p className={`text-xs truncate ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`}>
                {extractFileName(brandAssets.brandMarkWordMark.original)}
              </p>
            </div>
          )}
          
          {/* Upload area */}
          <div
            {...getRootPropsBrandMarkWord()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
              uploadingType === 'brandMarkWord' 
                ? 'opacity-50 pointer-events-none' 
                : darkMode ? 'hover:bg-[#181818]' : 'hover:bg-[#f5f5f5]'
            } ${
              brandAssets?.brandMarkWordMark?.original 
                ? 'border-green-300 dark:border-green-700' 
                : darkMode ? 'border-[#282828]' : 'border-[#d1d5db]'
            }`}
          >
            <input {...getInputPropsBrandMarkWord()} />
            <div className="space-y-2">
              <div className="flex justify-center">
                {uploadingType === 'brandMarkWord' ? (
                  <Lottie
                    animationData={darkMode ? nvgtrLoaderWhite : nvgtrLoader}
                    loop={true}
                    style={{ width: 24, height: 24 }}
                  />
                ) : (
                  <FiUpload className={`h-5 w-5 ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`} />
                )}
              </div>
              <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-[#030303]'}`}>
                {brandAssets?.brandMarkWordMark?.original ? 'Replace file' : 'Upload file'}
              </div>
              <p className={`text-xs ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`}>
                .svg or .ai only
              </p>
            </div>
          </div>
        </div>
      </div>
      </section>
      
      {/* Completion Status */}
      {bothAssetsUploaded && !isSubmitted && (
        <section className="pt-5">
          <div className={`border rounded-xl p-4 text-center ${
            darkMode 
              ? 'bg-green-900/20 border-green-700' 
              : 'bg-green-50 border-green-200'
          }`}>
            <FiCheckCircle className="h-6 w-6 text-green-500 mx-auto mb-2" />
            <p className={`text-sm font-medium ${darkMode ? 'text-green-200' : 'text-green-900'}`}>
              Assets uploaded! We'll review and activate them within 1-2 business days.
            </p>
          </div>
        </section>
      )}
      </div>
    </div>
  );
};

export default BrandSection; 